import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  SafetyInboxCase,
  SafetyIntakeState,
  SafetyReportAccessEntry,
  SafetyReportCategory,
  SafetyReportDetail,
  SafetyReportStatus,
  escalateSafetyReport,
  getSafetyInbox,
  openSafetyReport,
  triageSafetyReport,
} from '../api/safety-reports';
import { UTC_ZONE, formatShortDateTime } from '../time/zonedTime';
import {
  Drawer,
  Empty,
  Field,
  Note,
  OpsPage,
  Pill,
  Row,
  Section,
  Table,
  Value,
} from '../../../design-system/ops';

/**
 * Trust & Safety inbox — `/admin/safety` (UC-S09 STU-11-FR-08 → T&S, MC-09).
 *
 * The server decides who sees this: an operator holding the Trust & Safety role
 * itself, with MFA. Anyone else gets a 403, and this screen says so plainly
 * rather than pretending the inbox is empty — an operator who believes there
 * are no reports when they simply cannot see them is the wrong outcome.
 *
 * MC-09 intake states: received → triaged → escalated. Triage accepts the case
 * and records its urgency; an urgent case escalates to the on-call owner, and
 * an operator can escalate to the suplente when the titular is unavailable.
 * Closing and the preventive measures themselves are UC-A11 (Sprint 4); the
 * screen offers no action it cannot back. Every report opened here is logged
 * in the report's own file.
 */

const CATEGORY_LABELS: Record<SafetyReportCategory, string> = {
  FELT_UNSAFE: 'Se sintió inseguro',
  INAPPROPRIATE_CONTENT: 'Contenido inapropiado',
  PRIVATE_CONTACT_REQUEST: 'Pidió datos o contacto privado',
  BULLYING: 'Acoso o malos tratos',
  OTHER: 'Otra cosa',
};

const STATUS_LABELS: Record<SafetyReportStatus, string> = {
  RECEIVED: 'Recibido',
  TRIAGED: 'Evaluado',
  ESCALATED: 'Escalado',
};

const ACCESS_LABELS: Record<SafetyReportAccessEntry['action'], string> = {
  RECEIVED: 'Recibido',
  OPENED: 'Abierto',
  ALERTED: 'Alerta enviada',
  ALERT_FAILED: 'Alerta no entregada',
  TRIAGED: 'Evaluado',
  ESCALATED: 'Escalado',
};

const fmt = (iso: string) => `${formatShortDateTime(iso, UTC_ZONE)} UTC`;

const IntakeNote: React.FC<{ intake: SafetyIntakeState }> = ({ intake }) => {
  if (intake.flagOn && intake.ready) {
    return (
      <Note tone="warn">
        El formulario de reporte está activo. Las nueve condiciones de activación tienen evidencia.
      </Note>
    );
  }
  if (intake.flagOn) {
    return (
      <Note tone="bad">
        El interruptor está encendido, pero el formulario sigue apagado: faltan condiciones de
        activación. Revisa la operación de seguridad.
      </Note>
    );
  }
  return (
    <Note>
      El formulario de reporte está apagado. Solo se activa cuando la operación de seguridad cumple
      las nueve condiciones y el interruptor se enciende.
    </Note>
  );
};

const AdminSafetyInbox: React.FC = () => {
  const [inbox, setInbox] = useState<{
    intake: SafetyIntakeState;
    cases: SafetyInboxCase[];
  } | null>(null);
  const [error, setError] = useState<{ forbidden: boolean; text: string } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SafetyReportDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadInbox = useCallback(() => {
    let cancelled = false;
    getSafetyInbox()
      .then((v) => !cancelled && setInbox(v))
      .catch((e: any) => {
        if (cancelled) return;
        const forbidden = e?.status === 403 || /403|forbidden|Trust & Safety/i.test(e?.message);
        setError({ forbidden, text: e?.message || 'No se pudo cargar la bandeja' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadInbox(), [loadInbox]);

  const loadDetail = useCallback((reportId: string) => {
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    openSafetyReport(reportId)
      .then((d) => !cancelled && setDetail(d))
      .catch((e: any) => !cancelled && setDetailError(e?.message || 'No se pudo abrir el reporte'));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!openId) return undefined;
    return loadDetail(openId);
  }, [openId, loadDetail]);

  const act = async (run: () => Promise<unknown>) => {
    if (!openId) return;
    setBusy(true);
    setActionError(null);
    try {
      await run();
      loadDetail(openId);
      loadInbox();
    } catch (e: any) {
      setActionError(e?.message || 'No se pudo completar la acción');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <OpsPage title="Safety">
        <Note tone="bad">
          {error.forbidden
            ? 'Esta bandeja solo la puede leer el equipo de Trust & Safety. Tu rol no la incluye.'
            : error.text}
        </Note>
      </OpsPage>
    );
  }
  if (!inbox) {
    return (
      <OpsPage title="Safety">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const report = detail?.report;

  return (
    <OpsPage title="Safety" subtitle="Reportes de seguridad de los alumnos · solo Trust & Safety">
      <IntakeNote intake={inbox.intake} />
      <p>
        <Link to="/admin/safety/operations">
          Operación de seguridad y condiciones de activación
        </Link>
      </p>

      <Section title="Reportes recibidos" count={inbox.cases.length}>
        {inbox.cases.length === 0 ? (
          <Empty icon="🛡" title="No hay reportes" />
        ) : (
          <Table
            columns="170px 1.2fr 1fr 1fr 120px 90px"
            head={['Recibido', 'Tipo', 'Alumno', 'Clase', 'Estado', 'Prioridad']}
          >
            {inbox.cases.map((c) => (
              <Row
                key={c.caseId}
                onOpen={() => setOpenId(c.reportId)}
                label={`Abrir reporte recibido ${fmt(c.receivedAt)}`}
              >
                <span>
                  {fmt(c.receivedAt)}
                  <br />
                  <span className="mut">hace {c.hoursOpen} h</span>
                </span>
                <span>
                  {CATEGORY_LABELS[c.category] ?? c.category}
                  {c.drill ? <span className="mut"> · simulacro</span> : null}
                </span>
                <span className="mut">{c.studentId}</span>
                <span className="mut">
                  {c.enrollmentId ? `${c.enrollmentId}${c.seq ? ` · sesión ${c.seq}` : ''}` : '—'}
                </span>
                <span>
                  {STATUS_LABELS[c.status] ?? c.status}
                  {c.urgency === 'URGENT' ? <span className="mut"> · urgente</span> : null}
                </span>
                <span>
                  <Pill tone="bad">Alta</Pill>
                </span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title="Reporte de seguridad"
        meta={report ? fmt(report.receivedAt) : undefined}
      >
        {detailError ? <Note tone="bad">{detailError}</Note> : null}
        {!detail && !detailError ? <Note>Cargando…</Note> : null}
        {detail && report ? (
          <>
            <Field label="Tipo">
              <Value>{CATEGORY_LABELS[report.category] ?? report.category}</Value>
            </Field>
            <Field label="Lo que contó el alumno">
              <Value>{report.description || 'El alumno no escribió nada.'}</Value>
            </Field>
            <Field label="Contexto">
              <Value>
                Alumno {report.studentId} · Familia {report.reporterId}
                {report.enrollmentId ? ` · Inscripción ${report.enrollmentId}` : ''}
                {report.seq ? ` · Sesión ${report.seq}` : ''}
                {report.teacherId ? ` · Profesor ${report.teacherId}` : ''}
              </Value>
            </Field>
            <Field label="Estado">
              <Value>
                {STATUS_LABELS[report.status] ?? report.status}
                {report.assignedTo ? ` · asignado a ${report.assignedTo}` : ''}
                {report.urgency
                  ? ` · ${report.urgency === 'URGENT' ? 'urgente' : 'no urgente'}`
                  : ''}
                {report.escalatedTo ? ` · escalado a ${report.escalatedTo}` : ''}
              </Value>
            </Field>
            <Field label="Historial del expediente">
              {detail.access.map((a) => (
                <Value key={`${a.at}-${a.actorId}-${a.action}`}>
                  {ACCESS_LABELS[a.action] ?? a.action} · {a.actorId}
                  {a.detail ? ` · ${a.detail}` : ''} · {fmt(a.at)}
                </Value>
              ))}
            </Field>

            {actionError ? <Note tone="bad">{actionError}</Note> : null}
            {!report.triagedAt ? (
              <Field label="Evaluar">
                <button
                  type="button"
                  className="btn btn-p"
                  disabled={busy}
                  onClick={() => act(() => triageSafetyReport(report.reportId, 'URGENT'))}
                >
                  Aceptar como urgente
                </button>{' '}
                <button
                  type="button"
                  className="btn btn-o"
                  disabled={busy}
                  onClick={() => act(() => triageSafetyReport(report.reportId, 'STANDARD'))}
                >
                  Aceptar como no urgente
                </button>
              </Field>
            ) : null}
            <Field label="Escalar">
              <button
                type="button"
                className="btn btn-o"
                disabled={busy}
                onClick={() =>
                  act(() => escalateSafetyReport(report.reportId, 'PRIMARY_UNAVAILABLE'))
                }
              >
                Escalar al suplente (titular no disponible)
              </button>
            </Field>
            <Note>
              El profesor no sabe que este reporte existe. Las medidas preventivas y el cierre del
              caso llegan con UC-A11; mientras tanto se ejecutan por la vía provisional declarada en
              la operación de seguridad.
            </Note>
          </>
        ) : null}
      </Drawer>
    </OpsPage>
  );
};

export default AdminSafetyInbox;
